import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { 
  Building2, 
  Users, 
  FileText, 
  DollarSign,
  Calendar,
  Loader2,
  User,
  Eye,
  FileSpreadsheet,
  FileText as FileTextIcon,
  Search,
  GitBranch,
  Clock
} from 'lucide-react';
import { api } from '@/lib/api';
import { formatCurrency, formatDateTime } from '@/lib/formatters';
import { formatDate, parseCivilDateString } from '@/lib/utils';
import ClienteFornecedorModal from '@/components/ClientesFornecedores/ClienteFornecedorModal';

interface InadimplenciaClientsModalProps {
  isOpen: boolean;
  onClose: () => void;
  category: '1-2' | '3-6' | '7+' | null;
  selectedCompany?: string;
}

type SortKey = 'valor' | 'dias' | 'etapa';

interface ClienteData {
  id: string;
  nome: string;
  documento: string;
  email: string;
  titulos: any[];
  valorTotal: number;
  quantidadeTitulos: number;
  diasAtrasoMax?: number;
  titulosVencidos: number;
  titulosAbertos: number;
  titulosParciais: number;
  titulosFechados: number;
  valorVencido: number;
  valorAberto: number;
  valorParcial: number;
  valorFechado: number;
  empresa?: string;
  operador?: string;
  companyId?: string;
  company?: {
    id: string;
    name: string;
  };
  operadorObj?: {
    id: string;
    name: string;
    email: string;
  } | null;
  kanban?: {
    id: string;
    nome: string;
  } | null;
  kanbanEtapa?: {
    id: string;
    nome: string;
    cor: string;
  } | null;
  ultimaAtualizacaoEtapa?: string | null;
}

function operadorNome(cliente: ClienteData): string {
  return cliente.operadorObj?.name || cliente.operador || 'Sem operador';
}

export const InadimplenciaClientsModal: React.FC<InadimplenciaClientsModalProps> = ({
  isOpen,
  onClose,
  category,
  selectedCompany
}) => {
  const [selectedCliente, setSelectedCliente] = useState<ClienteData | null>(null);
  const [isClienteModalOpen, setIsClienteModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('valor');

  React.useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setSortKey('valor');
    }
  }, [isOpen, category]);

  // Função para abrir timeline do cliente em nova aba
  const handleOpenClienteTimeline = async (cliente: ClienteData) => {
    try {
      // Buscar dados completos do cliente
      const response = await api.get(`/clientes-fornecedores/${cliente.id}`);
      const clienteCompleto = response.data.data;
      
      // Criar uma página HTML simples com a timeline
      const htmlContent = `
        <!DOCTYPE html>
        <html lang="pt-BR">
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Timeline - ${cliente.nome}</title>
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                background: #f5f5f5;
                padding: 20px;
                color: #333;
              }
              .container {
                max-width: 1200px;
                margin: 0 auto;
                background: white;
                border-radius: 8px;
                box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                padding: 30px;
              }
              .header {
                border-bottom: 2px solid #3b82f6;
                padding-bottom: 20px;
                margin-bottom: 30px;
              }
              .header h1 {
                color: #1f2937;
                font-size: 24px;
                margin-bottom: 10px;
              }
              .header-info {
                display: flex;
                gap: 20px;
                flex-wrap: wrap;
                color: #6b7280;
                font-size: 14px;
              }
              .header-info span {
                display: flex;
                align-items: center;
                gap: 5px;
              }
              .timeline {
                position: relative;
                padding-left: 40px;
              }
              .timeline::before {
                content: '';
                position: absolute;
                left: 15px;
                top: 0;
                bottom: 0;
                width: 2px;
                background: #e5e7eb;
              }
              .timeline-item {
                position: relative;
                margin-bottom: 30px;
                padding: 20px;
                background: #f9fafb;
                border-radius: 8px;
                border-left: 4px solid #3b82f6;
              }
              .timeline-item::before {
                content: '';
                position: absolute;
                left: -32px;
                top: 25px;
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background: #3b82f6;
                border: 3px solid white;
                box-shadow: 0 0 0 2px #e5e7eb;
              }
              .timeline-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 10px;
              }
              .timeline-type {
                font-weight: 600;
                color: #3b82f6;
                font-size: 14px;
              }
              .timeline-date {
                color: #6b7280;
                font-size: 12px;
              }
              .timeline-content {
                color: #374151;
                line-height: 1.6;
                margin-bottom: 10px;
              }
              .timeline-user {
                color: #6b7280;
                font-size: 12px;
                display: flex;
                align-items: center;
                gap: 5px;
              }
              .empty-state {
                text-align: center;
                padding: 60px 20px;
                color: #9ca3af;
              }
              .empty-state svg {
                width: 64px;
                height: 64px;
                margin: 0 auto 20px;
                opacity: 0.5;
              }
              .timeline-section {
                margin-top: 15px;
                padding-top: 15px;
                border-top: 1px solid #e5e7eb;
              }
              .timeline-section-title {
                font-weight: 600;
                color: #374151;
                font-size: 13px;
                margin-bottom: 10px;
              }
              .titulos-list {
                display: flex;
                flex-direction: column;
                gap: 8px;
              }
              .titulo-item {
                padding: 10px;
                background: white;
                border-radius: 6px;
                border: 1px solid #e5e7eb;
              }
              .titulo-info {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 5px;
              }
              .titulo-numero {
                font-weight: 600;
                color: #1f2937;
                font-size: 13px;
              }
              .titulo-valor {
                font-weight: 600;
                color: #059669;
                font-size: 13px;
              }
              .titulo-detalhes {
                display: flex;
                gap: 15px;
                flex-wrap: wrap;
                font-size: 11px;
                color: #6b7280;
              }
              .comprovantes-list {
                display: flex;
                flex-direction: column;
                gap: 8px;
              }
              .comprovante-item {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 10px;
                background: white;
                border-radius: 6px;
                border: 1px solid #e5e7eb;
              }
              .comprovante-info {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 13px;
                color: #374151;
              }
              .comprovante-link {
                padding: 5px 12px;
                background: #3b82f6;
                color: white;
                text-decoration: none;
                border-radius: 4px;
                font-size: 12px;
                font-weight: 500;
                transition: background 0.2s;
              }
              .comprovante-link:hover {
                background: #2563eb;
              }
              .pagamentos-list {
                display: flex;
                flex-direction: column;
                gap: 8px;
              }
              .pagamento-item {
                padding: 10px;
                background: white;
                border-radius: 6px;
                border: 1px solid #e5e7eb;
              }
              .pagamento-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 8px;
              }
              .pagamento-valor {
                font-weight: 600;
                color: #059669;
                font-size: 14px;
              }
              .pagamento-status {
                padding: 3px 10px;
                border-radius: 12px;
                font-size: 11px;
                font-weight: 600;
              }
              .status-confirmado {
                background: #d1fae5;
                color: #059669;
              }
              .status-pendente {
                background: #fef3c7;
                color: #d97706;
              }
              .status-cancelado {
                background: #fee2e2;
                color: #dc2626;
              }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="header">
                <h1>Timeline - ${cliente.nome || 'Cliente'}</h1>
                <div class="header-info">
                  ${cliente.documento ? `<span>📄 Doc: ${cliente.documento}</span>` : ''}
                  ${cliente.email ? `<span>✉️ ${cliente.email}</span>` : ''}
                  ${cliente.quantidadeTitulos ? `<span>📋 ${cliente.quantidadeTitulos} título(s)</span>` : ''}
                  ${cliente.valorTotal ? `<span>💰 ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cliente.valorTotal)}</span>` : ''}
                  ${clienteCompleto.titulos && clienteCompleto.titulos.length > 0 
                    ? `<span>📊 Total de Títulos: ${clienteCompleto.titulos.length}</span>` 
                    : ''}
                </div>
              </div>
              
              ${clienteCompleto.titulos && clienteCompleto.titulos.length > 0 
                ? `
                <div style="margin-bottom: 30px; padding: 20px; background: #f9fafb; border-radius: 8px; border-left: 4px solid #3b82f6;">
                  <h2 style="font-size: 18px; color: #1f2937; margin-bottom: 15px;">📋 Títulos do Cliente</h2>
                  <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 15px;">
                    ${clienteCompleto.titulos
                      .sort((a: any, b: any) => {
                        // Ordenar do mais recente para o mais antigo
                        // Priorizar por createdAt, depois por vencimento
                        const dateA = a.createdAt
                          ? new Date(a.createdAt).getTime()
                          : a.vencimento
                            ? parseCivilDateString(a.vencimento).getTime()
                            : 0;
                        const dateB = b.createdAt
                          ? new Date(b.createdAt).getTime()
                          : b.vencimento
                            ? parseCivilDateString(b.vencimento).getTime()
                            : 0;
                        return dateB - dateA; // Mais recente primeiro
                      })
                      .map((titulo: any) => {
                      const vencimento = titulo.vencimento ? formatDate(titulo.vencimento) : 'N/A';
                      const statusColor = titulo.status === 'VENCIDO' ? '#dc2626' 
                        : titulo.status === 'PARCIAL' ? '#f59e0b'
                        : titulo.status === 'FECHADO' ? '#059669'
                        : '#3b82f6';
                      return `
                        <div style="padding: 15px; background: white; border-radius: 6px; border-left: 3px solid ${statusColor}; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                            <span style="font-weight: 600; color: #1f2937;">${titulo.numero || 'N/A'}</span>
                            <span style="padding: 3px 8px; background: ${statusColor}15; color: ${statusColor}; border-radius: 12px; font-size: 11px; font-weight: 600;">${titulo.status || 'N/A'}</span>
                          </div>
                          <div style="font-size: 14px; font-weight: 600; color: #059669; margin-bottom: 5px;">
                            ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(titulo.valor || 0)}
                          </div>
                          <div style="font-size: 12px; color: #6b7280; display: flex; gap: 15px; flex-wrap: wrap;">
                            <span>Vencimento: ${vencimento}</span>
                            ${titulo.saldo ? `<span>Saldo: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(titulo.saldo)}</span>` : ''}
                          </div>
                        </div>
                      `;
                    }).join('')}
                  </div>
                </div>
                `
                : ''
              }
              <div class="timeline">
                ${clienteCompleto.feedbacks && clienteCompleto.feedbacks.length > 0 
                  ? clienteCompleto.feedbacks
                      .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                      .map((feedback: any) => {
                      const tipoLabel = feedback.tipo === 'LIGACAO' ? '📞 Ligação' 
                        : feedback.tipo === 'EMAIL' ? '✉️ Email'
                        : feedback.tipo === 'WHATSAPP' ? '💬 WhatsApp'
                        : feedback.tipo === 'VISITA' ? '📍 Visita'
                        : '💬 Contato';
                      const data = new Date(feedback.createdAt);
                      const dataFormatada = data.toLocaleDateString('pt-BR', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      });
                      // Mesma origem no dev (Vite faz proxy de /api para a API)
                      const apiBaseUrl = window.location.origin;
                      
                      const getFileUrl = (fileUrl: string) => {
                        if (!fileUrl) return '';
                        if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
                          return fileUrl;
                        }
                        if (fileUrl.startsWith('/api/v1')) {
                          return `${apiBaseUrl}${fileUrl}`;
                        }
                        if (fileUrl.startsWith('/uploads')) {
                          return `${window.location.origin}${fileUrl}`;
                        }
                        return `${apiBaseUrl}/api/v1${fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`}`;
                      };
                      
                      // Títulos negociados
                      const titulosHtml = feedback.titulosNegociadosData && feedback.titulosNegociadosData.length > 0
                        ? `
                          <div class="timeline-section">
                            <div class="timeline-section-title">📋 Títulos Relacionados (${feedback.titulosNegociadosData.length})</div>
                            <div class="titulos-list">
                              ${feedback.titulosNegociadosData
                                .sort((a: any, b: any) => {
                                  // Ordenar do mais recente para o mais antigo
                                  const dateA = a.createdAt
                                    ? new Date(a.createdAt).getTime()
                                    : a.vencimento
                                      ? parseCivilDateString(a.vencimento).getTime()
                                      : 0;
                                  const dateB = b.createdAt
                                    ? new Date(b.createdAt).getTime()
                                    : b.vencimento
                                      ? parseCivilDateString(b.vencimento).getTime()
                                      : 0;
                                  return dateB - dateA; // Mais recente primeiro
                                })
                                .map((titulo: any) => {
                                const vencimento = titulo.vencimento ? formatDate(titulo.vencimento) : 'N/A';
                                return `
                                  <div class="titulo-item">
                                    <div class="titulo-info">
                                      <span class="titulo-numero">${titulo.numero || 'N/A'}</span>
                                      <span class="titulo-valor">${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(titulo.valor || 0)}</span>
                                    </div>
                                    <div class="titulo-detalhes">
                                      <span>Status: ${titulo.status || 'N/A'}</span>
                                      <span>Vencimento: ${vencimento}</span>
                                      ${titulo.company?.name ? `<span>Empresa: ${titulo.company.name}</span>` : ''}
                                    </div>
                                  </div>
                                `;
                              }).join('')}
                            </div>
                          </div>
                        `
                        : '';
                      
                      // Comprovantes/Anexos
                      const comprovantesHtml = feedback.comprovantes && feedback.comprovantes.length > 0
                        ? `
                          <div class="timeline-section">
                            <div class="timeline-section-title">📎 Anexos/Comprovantes (${feedback.comprovantes.length})</div>
                            <div class="comprovantes-list">
                              ${feedback.comprovantes.map((comprovante: any) => {
                                const fileUrl = getFileUrl(comprovante.url);
                                const fileSize = comprovante.fileSize ? (comprovante.fileSize / 1024).toFixed(2) + ' KB' : '';
                                return `
                                  <div class="comprovante-item">
                                    <div class="comprovante-info">
                                      <span>📄 ${comprovante.fileName || comprovante.originalName || 'Arquivo'}</span>
                                      ${fileSize ? `<span style="color: #6b7280; font-size: 11px;">(${fileSize})</span>` : ''}
                                    </div>
                                    ${fileUrl ? `<a href="${fileUrl}" target="_blank" class="comprovante-link">Abrir</a>` : ''}
                                  </div>
                                `;
                              }).join('')}
                            </div>
                          </div>
                        `
                        : '';
                      
                      // Histórico de Pagamentos
                      const pagamentosHtml = feedback.historicoPagamentos && feedback.historicoPagamentos.length > 0
                        ? `
                          <div class="timeline-section">
                            <div class="timeline-section-title">💰 Pagamentos Registrados (${feedback.historicoPagamentos.length})</div>
                            <div class="pagamentos-list">
                              ${feedback.historicoPagamentos.map((pagamento: any) => {
                                const dataPagamento = pagamento.dataPagamento ? new Date(pagamento.dataPagamento).toLocaleDateString('pt-BR') : 'N/A';
                                const statusClass = pagamento.statusPagamento === 'CONFIRMADO' ? 'status-confirmado'
                                  : pagamento.statusPagamento === 'PENDENTE' ? 'status-pendente'
                                  : 'status-cancelado';
                                const statusLabel = pagamento.statusPagamento === 'CONFIRMADO' ? 'Confirmado'
                                  : pagamento.statusPagamento === 'PENDENTE' ? 'Pendente'
                                  : pagamento.statusPagamento === 'CANCELADO' ? 'Cancelado'
                                  : pagamento.statusPagamento || 'N/A';
                                return `
                                  <div class="pagamento-item">
                                    <div class="pagamento-header">
                                      <span class="pagamento-valor">${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(pagamento.valorNegociado || pagamento.valorPago || 0)}</span>
                                      <span class="pagamento-status ${statusClass}">${statusLabel}</span>
                                    </div>
                                    <div style="font-size: 12px; color: #6b7280;">
                                      <div>Título: ${pagamento.titulo?.numero || 'N/A'}</div>
                                      <div>Data: ${dataPagamento}</div>
                                      ${pagamento.formaPagamento ? `<div>Forma: ${pagamento.formaPagamento}</div>` : ''}
                                      ${pagamento.observacoes ? `<div style="margin-top: 5px; font-style: italic;">${pagamento.observacoes}</div>` : ''}
                                    </div>
                                  </div>
                                `;
                              }).join('')}
                            </div>
                          </div>
                        `
                        : '';
                      
                      return `
                        <div class="timeline-item">
                          <div class="timeline-header">
                            <span class="timeline-type">${tipoLabel}</span>
                            <span class="timeline-date">${dataFormatada}</span>
                          </div>
                          <div class="timeline-content">${feedback.descricao || 'Sem descrição'}</div>
                          ${feedback.user ? `<div class="timeline-user">👤 ${feedback.user.name || 'Usuário'}</div>` : ''}
                          ${titulosHtml}
                          ${comprovantesHtml}
                          ${pagamentosHtml}
                        </div>
                      `;
                    }).join('')
                  : `
                    <div class="empty-state">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>
                      <p>Nenhum contato registrado</p>
                      <p style="font-size: 12px; margin-top: 5px;">Os contatos aparecerão aqui quando forem registrados</p>
                    </div>
                  `
                }
              </div>
            </div>
          </body>
        </html>
      `;
      
      // Abrir nova aba com o conteúdo HTML
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(htmlContent);
        newWindow.document.close();
      }
    } catch (error) {
      console.error('Erro ao abrir timeline:', error);
      // Fallback: abrir página de clientes
      window.open(`/clientes-fornecedores?clienteId=${cliente.id}`, '_blank');
    }
  };

  const handleClienteClick = (cliente: ClienteData) => {
    // Debug: verificar dados recebidos
    console.log('🔍 Dados do cliente recebidos:', {
      id: cliente.id,
      nome: cliente.nome,
      empresa: cliente.empresa,
      companyId: cliente.companyId,
      company: cliente.company,
      titulos: cliente.titulos?.length || 0
    });
    
    // Transformar dados da inadimplência para o formato esperado pelo ClienteFornecedorModal
    const clienteFormatted = {
      id: cliente.id,
      codigo: cliente.documento || '',
      nome: cliente.nome,
      descricao: '',
      whatsapp: '',
      email: cliente.email || '',
      companyId: cliente.titulos[0]?.companyId || '',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      company: {
        id: cliente.companyId || cliente.titulos[0]?.companyId || '',
        name: cliente.company?.name || cliente.empresa || cliente.titulos[0]?.company?.name || cliente.titulos[0]?.empresa || 'Empresa não identificada'
      },
      titulosEmAberto: cliente.titulosAbertos || 0,
      valorTotalEmAberto: cliente.valorAberto || 0,
      titulosVencidos: cliente.titulosVencidos || 0,
      valorTotalVencido: cliente.valorVencido || 0,
      totalTitulos: cliente.quantidadeTitulos || 0,
      operadorAtual: cliente.operadorObj || (cliente.operador ? {
        id: '',
        name: typeof cliente.operador === 'string' ? cliente.operador : (cliente.operador as any)?.name || '',
        email: typeof cliente.operador === 'string' ? '' : (cliente.operador as any)?.email || ''
      } : undefined),
      titulos: cliente.titulos || [],
      feedbacks: [],
      historicoContatos: [],
      _count: {
        feedbacks: 0,
        titulos: cliente.quantidadeTitulos || 0
        }
      };
      
      // Debug: verificar dados formatados
      console.log('🔍 Dados formatados para o modal:', {
        id: clienteFormatted.id,
        nome: clienteFormatted.nome,
        companyId: clienteFormatted.companyId,
        companyName: clienteFormatted.company.name,
        titulos: clienteFormatted.titulos?.length || 0
      });
      
      setSelectedCliente(clienteFormatted as any);
      setIsClienteModalOpen(true);
  };

  const handleCloseClienteModal = () => {
    setIsClienteModalOpen(false);
    setSelectedCliente(null);
  };

  // Mapear categoria para faixa de inadimplência
  const getFaixaInadimplencia = (category: string | null) => {
    switch (category) {
      case '1-2':
        return '1-2 dias';
      case '3-6':
        return '3-6 dias';
      case '7+':
        return '7+ dias';
      default:
        return '';
    }
  };

  const { data: clientesData, isLoading, error } = useQuery({
    queryKey: ['aging-clientes', category, selectedCompany],
    queryFn: async () => {
      if (!category) return { clientes: [] };
      
      const params = new URLSearchParams();
      params.append('faixa', getFaixaInadimplencia(category));
      if (selectedCompany) {
        params.append('companyId', selectedCompany);
      }
      
      const response = await api.get(`/reports/aging/clientes?${params.toString()}`);
      return response.data.data;
    },
    enabled: isOpen && !!category,
    retry: 1,
  });

  const clientes: ClienteData[] = clientesData?.clientes || [];

  const clientesFiltrados = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let list = clientes;
    if (q) {
      list = list.filter((c) => {
        const op = operadorNome(c).toLowerCase();
        const funil = (c.kanban?.nome || '').toLowerCase();
        const etapa = (c.kanbanEtapa?.nome || '').toLowerCase();
        return (
          (c.nome || '').toLowerCase().includes(q) ||
          (c.documento || '').toLowerCase().includes(q) ||
          op.includes(q) ||
          funil.includes(q) ||
          etapa.includes(q)
        );
      });
    }
    const sorted = [...list];
    sorted.sort((a, b) => {
      if (sortKey === 'dias') {
        return (b.diasAtrasoMax || 0) - (a.diasAtrasoMax || 0);
      }
      if (sortKey === 'etapa') {
        const ta = a.ultimaAtualizacaoEtapa ? new Date(a.ultimaAtualizacaoEtapa).getTime() : 0;
        const tb = b.ultimaAtualizacaoEtapa ? new Date(b.ultimaAtualizacaoEtapa).getTime() : 0;
        return tb - ta;
      }
      return (b.valorTotal || 0) - (a.valorTotal || 0);
    });
    return sorted;
  }, [clientes, searchTerm, sortKey]);

  // Agrupar clientes filtrados por empresa (sem inventar etapa)
  const clientesPorEmpresa = clientesFiltrados.reduce(
    (
      acc: Record<string, { clientes: ClienteData[]; empresa: { id: string; name: string } }>,
      cliente: ClienteData
    ) => {
      const empresaId =
        cliente.companyId ||
        cliente.company?.id ||
        cliente.titulos?.[0]?.companyId ||
        'sem-empresa';
      const empresaName =
        cliente.company?.name ||
        cliente.empresa ||
        cliente.titulos?.[0]?.company?.name ||
        cliente.titulos?.[0]?.empresa ||
        'Empresa não identificada';

      if (!acc[empresaId]) {
        acc[empresaId] = {
          clientes: [],
          empresa: { id: empresaId, name: empresaName },
        };
      }
      acc[empresaId].clientes.push(cliente);
      return acc;
    },
    {}
  );

  // Calcular totais por empresa
  const getTotaisPorEmpresa = (clientes: ClienteData[]) => {
    return clientes.reduce((acc, cliente) => {
      acc.totalTitulos += cliente.quantidadeTitulos;
      acc.valorTotal += cliente.valorTotal;
      acc.totalClientes += 1;
      return acc;
    }, { totalTitulos: 0, valorTotal: 0, totalClientes: 0 });
  };

  const getCategoryTitle = (category: string | null) => {
    switch (category) {
      case '1-2':
        return '1 a 2 Dias Vencidos';
      case '3-6':
        return '3 a 6 Dias Vencidos';
      case '7+':
        return 'Acima de 7 Dias Vencidos';
      default:
        return 'Clientes';
    }
  };

  const getCategoryColor = (category: string | null) => {
    switch (category) {
      case '1-2':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case '3-6':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case '7+':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-500/20 dark:text-red-200 dark:border-red-800 dark:bg-red-500/20 dark:text-red-200 dark:border-red-800';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Função para exportar para Excel (CSV)
  const handleExportExcel = (clientesExport: ClienteData[], empresaNome: string) => {
    try {
      const headers = [
        'Cliente',
        'Documento',
        'Qtd. Títulos (faixa)',
        'Valor Total (faixa)',
        'Dias atraso (máx)',
        'Títulos Vencidos',
        'Títulos Abertos',
        'Títulos Pagos',
        'Títulos Parciais',
        'Valor Vencido',
        'Valor Aberto',
        'Valor Pago',
        'Valor Parcial',
        'Operador',
        'Funil',
        'Etapa',
        'Última atualização etapa',
        'Empresa'
      ];

      const rows = clientesExport.map((cliente) => [
        cliente.nome || '',
        cliente.documento || '',
        cliente.quantidadeTitulos || 0,
        cliente.valorTotal || 0,
        cliente.diasAtrasoMax || 0,
        cliente.titulosVencidos || 0,
        cliente.titulosAbertos || 0,
        cliente.titulosFechados || 0,
        cliente.titulosParciais || 0,
        cliente.valorVencido || 0,
        cliente.valorAberto || 0,
        cliente.valorFechado || 0,
        cliente.valorParcial || 0,
        operadorNome(cliente),
        cliente.kanban?.nome || 'Sem funil',
        cliente.kanbanEtapa?.nome || 'Sem etapa',
        cliente.ultimaAtualizacaoEtapa
          ? formatDateTime(cliente.ultimaAtualizacaoEtapa)
          : 'Sem histórico',
        empresaNome || cliente.empresa || 'Empresa não identificada'
      ]);

      // Combinar cabeçalhos e linhas
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          // Escapar vírgulas e aspas
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(','))
      ].join('\n');

      // Criar blob e fazer download
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = `inadimplencia_clientes_${empresaNome.replace(/[^a-z0-9]/gi, '_')}_${getCategoryTitle(category).replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao exportar para Excel:', error);
      alert('Erro ao exportar para Excel');
    }
  };

  // Função para exportar para PDF
  const handleExportPDF = (clientesExport: ClienteData[], empresaNome: string) => {
    try {
      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title>Relatório de Inadimplência - ${empresaNome}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                color: #333;
                font-size: 11px;
              }
              h1 {
                color: #1f2937;
                border-bottom: 2px solid #3b82f6;
                padding-bottom: 10px;
                margin-bottom: 20px;
              }
              h2 {
                color: #4b5563;
                margin-top: 30px;
                margin-bottom: 15px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 30px;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
              }
              th {
                background-color: #3b82f6;
                color: white;
                font-weight: bold;
              }
              tr:nth-child(even) {
                background-color: #f9fafb;
              }
              .total-row {
                background-color: #e5e7eb;
                font-weight: bold;
              }
              .header-info {
                margin-bottom: 20px;
                padding: 15px;
                background-color: #f3f4f6;
                border-radius: 5px;
              }
              .header-info p {
                margin: 5px 0;
              }
            </style>
          </head>
          <body>
            <h1>Relatório de Inadimplência - ${empresaNome}</h1>
            <div class="header-info">
              <p><strong>Categoria:</strong> ${getCategoryTitle(category)}</p>
              <p><strong>Data de Geração:</strong> ${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR')}</p>
              <p><strong>Total de Clientes:</strong> ${clientesExport.length}</p>
              <p><strong>Total de Títulos (faixa):</strong> ${clientesExport.reduce((sum, c) => sum + (c.quantidadeTitulos || 0), 0)}</p>
              <p><strong>Valor Total (faixa):</strong> ${formatCurrency(clientesExport.reduce((sum, c) => sum + (c.valorTotal || 0), 0))}</p>
            </div>
            <h2>Detalhamento por Cliente</h2>
            <table>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Doc</th>
                  <th>Valor faixa</th>
                  <th>Dias</th>
                  <th>Venc.</th>
                  <th>Abertos</th>
                  <th>Pagos</th>
                  <th>Parc.</th>
                  <th>Operador</th>
                  <th>Funil</th>
                  <th>Etapa</th>
                  <th>Últ. atualização etapa</th>
                </tr>
              </thead>
              <tbody>
                ${clientesExport.map(cliente => `
                  <tr>
                    <td>${cliente.nome || ''}</td>
                    <td>${cliente.documento || ''}</td>
                    <td>${formatCurrency(cliente.valorTotal || 0)}</td>
                    <td>${cliente.diasAtrasoMax || 0}</td>
                    <td>${cliente.titulosVencidos || 0}</td>
                    <td>${cliente.titulosAbertos || 0}</td>
                    <td>${cliente.titulosFechados || 0}</td>
                    <td>${cliente.titulosParciais || 0}</td>
                    <td>${operadorNome(cliente)}</td>
                    <td>${cliente.kanban?.nome || '—'}</td>
                    <td>${cliente.kanbanEtapa?.nome || '—'}</td>
                    <td>${cliente.ultimaAtualizacaoEtapa ? formatDateTime(cliente.ultimaAtualizacaoEtapa) : 'Sem histórico'}</td>
                  </tr>
                `).join('')}
                <tr class="total-row">
                  <td colspan="2"><strong>TOTAL</strong></td>
                  <td><strong>${formatCurrency(clientesExport.reduce((sum, c) => sum + (c.valorTotal || 0), 0))}</strong></td>
                  <td></td>
                  <td><strong>${clientesExport.reduce((sum, c) => sum + (c.titulosVencidos || 0), 0)}</strong></td>
                  <td><strong>${clientesExport.reduce((sum, c) => sum + (c.titulosAbertos || 0), 0)}</strong></td>
                  <td><strong>${clientesExport.reduce((sum, c) => sum + (c.titulosFechados || 0), 0)}</strong></td>
                  <td><strong>${clientesExport.reduce((sum, c) => sum + (c.titulosParciais || 0), 0)}</strong></td>
                  <td colspan="4"></td>
                </tr>
              </tbody>
            </table>
          </body>
        </html>
      `;

      // Abrir nova janela e imprimir (que permite salvar como PDF)
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      console.error('Erro ao exportar para PDF:', error);
      alert('Erro ao exportar para PDF');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getCategoryTitle(category)}
      size="xl"
    >
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className={`inline-flex items-center px-3 py-1 rounded-full border text-sm font-medium ${getCategoryColor(category)}`}>
            <Calendar className="w-4 h-4 mr-1" />
            {getCategoryTitle(category)}
          </div>
          <div className="text-sm text-muted-foreground">
            {searchTerm.trim()
              ? `${clientesFiltrados.length} de ${clientes.length} cliente${clientes.length !== 1 ? 's' : ''}`
              : `${clientes.length} cliente${clientes.length !== 1 ? 's' : ''} encontrado${clientes.length !== 1 ? 's' : ''}`}
          </div>
        </div>

        {!isLoading && !error && clientes.length > 0 && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nome, documento, operador ou funil…"
                className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              aria-label="Ordenar clientes"
            >
              <option value="valor">Ordenar por valor</option>
              <option value="dias">Ordenar por dias de atraso</option>
              <option value="etapa">Ordenar por última atualização da etapa</option>
            </select>
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span>Carregando clientes...</span>
          </div>
        )}

        {error && (
          <div className="text-center py-8 text-red-600">
            Erro ao carregar dados dos clientes
          </div>
        )}

        {!isLoading && !error && (
          <div className="space-y-4">
            {clientes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum cliente encontrado nesta categoria
              </div>
            ) : Object.keys(clientesPorEmpresa).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum cliente corresponde à busca
              </div>
            ) : (
              Object.entries(clientesPorEmpresa).map(([empresaId, empresaData]) => {
                const totais = getTotaisPorEmpresa(empresaData.clientes);
                const empresa = empresaData.empresa;

                return (
                  <Card key={empresaId}>
                    <CardHeader className="pb-3">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-center space-x-2">
                          <Building2 className="w-5 h-5 text-blue-600" />
                          <CardTitle className="text-lg">{empresa.name}</CardTitle>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="flex flex-wrap items-center gap-3 text-sm font-semibold">
                            <span className="inline-flex items-center gap-1">
                              <Users className="w-4 h-4 text-blue-600" />
                              {totais.totalClientes} cliente{totais.totalClientes !== 1 ? 's' : ''}
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <FileText className="w-4 h-4 text-blue-600" />
                              {totais.totalTitulos} título{totais.totalTitulos !== 1 ? 's' : ''}
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <DollarSign className="w-4 h-4 text-green-600" />
                              {formatCurrency(totais.valorTotal)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExportExcel(empresaData.clientes, empresa.name);
                              }}
                              className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
                              title="Exportar para Excel"
                            >
                              <FileSpreadsheet className="w-4 h-4" />
                              <span>Excel</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExportPDF(empresaData.clientes, empresa.name);
                              }}
                              className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                              title="Exportar para PDF"
                            >
                              <FileTextIcon className="w-4 h-4" />
                              <span>PDF</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {empresaData.clientes.map((cliente) => (
                          <div
                            key={cliente.id}
                            className="flex flex-col gap-2 rounded-lg border bg-muted/40 p-3 cursor-pointer transition-colors hover:bg-muted/70 sm:flex-row sm:items-start sm:justify-between"
                            onClick={() => handleClienteClick(cliente)}
                          >
                            <div className="min-w-0 flex-1 space-y-1.5">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <div className="font-medium text-foreground leading-tight">
                                    {cliente.nome}
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    {cliente.quantidadeTitulos} título
                                    {cliente.quantidadeTitulos !== 1 ? 's' : ''} na faixa
                                    {cliente.documento ? ` · Doc: ${cliente.documento}` : ''}
                                    {cliente.diasAtrasoMax
                                      ? ` · até ${cliente.diasAtrasoMax} dia${cliente.diasAtrasoMax !== 1 ? 's' : ''} atraso`
                                      : ''}
                                  </div>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenClienteTimeline(cliente);
                                  }}
                                  className="flex shrink-0 items-center gap-1 text-xs text-blue-600 hover:underline"
                                  title="Abrir timeline do cliente em nova aba"
                                >
                                  <Eye className="w-3 h-3" />
                                  <span>Ver histórico</span>
                                </button>
                              </div>

                              <div className="flex flex-wrap gap-1.5">
                                {(cliente.titulosVencidos || 0) > 0 && (
                                  <Badge variant="destructive" className="text-xs">
                                    {cliente.titulosVencidos} vencido
                                    {cliente.titulosVencidos !== 1 ? 's' : ''}
                                  </Badge>
                                )}
                                {(cliente.titulosAbertos || 0) > 0 && (
                                  <Badge variant="outline" className="text-xs">
                                    {cliente.titulosAbertos} aberto
                                    {cliente.titulosAbertos !== 1 ? 's' : ''}
                                  </Badge>
                                )}
                                {(cliente.titulosFechados || 0) > 0 && (
                                  <Badge className="text-xs bg-emerald-600 hover:bg-emerald-600">
                                    {cliente.titulosFechados} pago
                                    {cliente.titulosFechados !== 1 ? 's' : ''}
                                  </Badge>
                                )}
                                {(cliente.titulosParciais || 0) > 0 && (
                                  <Badge variant="secondary" className="text-xs">
                                    {cliente.titulosParciais} parcial
                                    {cliente.titulosParciais !== 1 ? 's' : ''}
                                  </Badge>
                                )}
                              </div>

                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400">
                                  <User className="w-3 h-3" />
                                  {operadorNome(cliente)}
                                </span>
                                <span className="inline-flex items-center gap-1">
                                  <GitBranch className="w-3 h-3" />
                                  {cliente.kanban?.nome || 'Sem funil'}
                                </span>
                                <span className="inline-flex items-center gap-1">
                                  <span
                                    className="h-2 w-2 rounded-full"
                                    style={{
                                      backgroundColor: cliente.kanbanEtapa?.cor || '#6b7280',
                                    }}
                                  />
                                  {cliente.kanbanEtapa?.nome || 'Sem etapa'}
                                </span>
                                <span className="inline-flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {cliente.ultimaAtualizacaoEtapa
                                    ? formatDateTime(cliente.ultimaAtualizacaoEtapa)
                                    : 'Sem histórico'}
                                </span>
                              </div>
                            </div>

                            <div className="text-left sm:text-right sm:pl-4 shrink-0">
                              <div className="font-semibold text-foreground">
                                {formatCurrency(cliente.valorTotal)}
                              </div>
                              <div className="text-xs text-muted-foreground">valor na faixa</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Modal do Cliente com Histórico */}
      {selectedCliente && isClienteModalOpen && (
        <ClienteFornecedorModal
          cliente={selectedCliente as any}
          isOpen={isClienteModalOpen}
          onClose={handleCloseClienteModal}
        />
      )}
    </Modal>
  );
};
